import { AppRoutes } from './routes'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { useOfflineStore } from './stores/offlineStore'
import { useAuthStore } from './stores/authStore'
import { useSubscriptionStore } from './stores/subscriptionStore'
import { useCreditsStore } from './stores/creditsStore'
import { useFeatureFlagsStore } from './stores/featureFlagsStore'
import OfflineIndicator from './components/OfflineIndicator'
import { useSubscriptionCache } from './hooks/useSubscriptionCache'
import {
  getDocumentTitle,
  getBrandLogoPath,
  getBrandAppleTouchIconPath,
  isDreambooth
} from './utils/brandUtils'
import {
  sendAppSettingsTelemetry,
  sendDeviceHeartbeat,
  sendDeviceCloseSignal,
  HEARTBEAT_INTERVAL_MINUTES
} from './utils/sendAppSettingsTelemetry'
import { initUsbAutoGrantSync } from './utils/cameras/usbAutoGrant'
import { installRendererLifecycle } from './utils/telemetry/installRendererLifecycle'
import {
  installGracefulQuitHandler,
  reportPreviousGoodbye
} from './utils/cameras/gracefulQuit'
import { useSessionStateStore } from './stores/sessionState'
import {
  getHostAppVersion,
  getInternetStatus,
  getSubscriptionState,
  hasElectronPreload,
  onInternetStatusChanged,
  reportRemoteUpdateEnabled,
  resolveStableDeviceId
} from '@renderer/lib/runtime'
import { useSettingsStore, settingsStoreWasFresh } from './stores/settingsStore'
import { sweepLeftoverTerminalOrders } from './utils/openTerminalOrders'
import { prefetchSessionId } from './utils/sessionIdSource'

/** Point an existing <link> at a new href, or add one if the document has none. */
function setHeadLink(selector: string, rel: string, href: string): void {
  const existing = document.querySelector<HTMLLinkElement>(selector)
  if (existing) {
    existing.href = href
    return
  }
  const created = document.createElement('link')
  created.rel = rel
  created.href = href
  document.head.appendChild(created)
}

function App(): JSX.Element {
  const { i18n } = useTranslation()
  const [isOnline, setIsOnline] = useState<boolean>(true) // Assume online initially
  const { setAppVersion } = useOfflineStore()
  // Browser-gesture suppression (class, touch guards, history pin) lives in
  // KioskGestureGuard, mounted inside the Router in routes/index.tsx.
  useSubscriptionCache()

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language')
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage)
    }

    document.title = getDocumentTitle()
    setHeadLink("link[rel~='icon']", 'icon', isDreambooth() ? '/favicon.ico' : getBrandLogoPath())
    // The Home Screen icon, for an iPad booth added via Add to Home Screen.
    // iOS reads this link out of the LIVE DOM at the moment the operator taps
    // it, so swapping it here — long before anyone reaches the Share sheet — is
    // enough to put the right brand on the Home Screen. `rel~='icon'` above
    // does not match this one: "apple-touch-icon" is a single token.
    setHeadLink("link[rel='apple-touch-icon']", 'apple-touch-icon', getBrandAppleTouchIconPath())

    void getInternetStatus().then(setIsOnline)
    const unsubscribe = onInternetStatusChanged(setIsOnline)
    void getHostAppVersion().then((v) => {
      if (v) setAppVersion(v)
    })
    return (): void => {
      unsubscribe()
    }
  }, [i18n])

  // Electron-only bootstrap: init cookie, load cached subscription from SQLite, then verify in background
  useEffect(() => {
    if (!hasElectronPreload()) return

    // Did the previous booth UI die without closing (crash, hang, power cut)?
    // Reported once as a BoothEvent; this one keeps its own record from now on.
    // First, so a renderer that dies during the rest of bootstrap is still caught.
    installRendererLifecycle()

    // Camera Auto-Grant: mirror the `camera_auto_grant` flag into the main process
    // (persisted flags hydrate immediately; the subscription re-sends after the
    // post-login fetch below resolves fresh values). Old mains: harmless no-op.
    initUsbAutoGrantSync()

    // The camera goodbye: when main is about to close the window it asks us to release
    // the cameras first (gp_camera_exit), so the body is left in a clean state instead
    // of an open PTP session. Old mains never ask — inert there. See
    // src/shared/gracefulClose.ts.
    installGracefulQuitHandler()
    // Whether the PREVIOUS close actually released the camera — parked in localStorage
    // by that close, reported here because a shutdown cannot flush its own log.
    void reportPreviousGoodbye()

    const bootstrap = async (): Promise<void> => {
      try {
        // 0. Stabilize device identity BEFORE anything validates with it.
        //    Established id → mirrored into main-process SQLite (no change here).
        //    Fresh localStorage → recover the durable id so the server-side
        //    subscription binding survives the wipe. Old shells return null.
        try {
          const currentDeviceId = useSettingsStore.getState().deviceId
          const stableDeviceId = await resolveStableDeviceId({
            candidate: currentDeviceId,
            rendererHadStore: !settingsStoreWasFresh
          })
          if (stableDeviceId && stableDeviceId !== currentDeviceId) {
            console.log('[APP] Device identity restored from durable store')
            useSettingsStore.getState().setSetting('deviceId', stableDeviceId)
          }
        } catch (identityError) {
          console.warn('[APP] Device identity sync failed (non-fatal):', identityError)
        }

        // 1. Initialize cookie (moved from ProtectedRoute)
        const cookieOk = await useAuthStore.getState().initializeCookie()

        // 2. Cross-check persisted subscription against SQLite (has server-aware expiry data).
        //    subscriptionStore and creditsStore now use Zustand persist, so they already
        //    have the last-known values from localStorage. SQLite is the authority on
        //    whether the subscription has actually expired (endDate + serverTimeOffset).
        const cached = await getSubscriptionState()
        if (cached?.isValid) {
          const now = Math.floor(Date.now() / 1000)
          const adjustedNow = now + (cached.serverTimeOffset || 0)
          if (!cached.endDate || cached.endDate > adjustedNow) {
            useSubscriptionStore.getState().setSubscription({
              type: cached.type || 'free',
              plan: cached.plan || null,
              isValid: true,
              deviceId: cached.deviceId || undefined,
              activationCode: cached.activationCode || undefined
            })
            useCreditsStore.setState({
              credits: 'Unlimited',
              package: cached.type || 'free',
              error: null
            })
          } else {
            // SQLite says subscription expired -- clear stale persisted state
            useSubscriptionStore.getState().clearSubscription()
            useCreditsStore.setState({ credits: 0, package: 'free' })
          }
        } else if (useSubscriptionStore.getState().isValid) {
          // localStorage says valid but SQLite disagrees (cleared/missing) -- reconcile
          // Don't clear yet; the background fetch (step 3) will re-validate.
          // This handles the case where SQLite was wiped but localStorage wasn't.
        }

        // 3. Start background subscription + credits validation
        //    Subscription/credits only run when online AND cookie init succeeded so we do
        //    not spam the API with unauthenticated requests before the session cookie
        //    exists. Feature flags are deliberately NOT under that gate — see below.
        const isCurrentlyOnline = await getInternetStatus()
        if (cookieOk && useAuthStore.getState().isAuthenticated) {
          if (isCurrentlyOnline) {
            useCreditsStore.getState().fetchSubscriptionAndCredits()
          }
        } else if (!cookieOk) {
          console.log(
            '[APP] Cookie init failed — skipping subscription fetch, user will be redirected to login'
          )
        }
        // Resolve feature availability for this account. Deliberately NOT gated on
        // the cookie: /api/features also accepts the booth's bearer token, so a
        // booth whose cookie lapsed can still resolve its real flags — and when it
        // truly can't be identified, the store keeps the last authenticated map
        // rather than adopting the anonymous defaults. Gating this on `cookieOk`
        // is what froze Bahela Kotak's flags at `{}` after its session was cleared.
        // Offline keeps the persisted flags from the previous session.
        void useFeatureFlagsStore.getState().fetchFlags(isCurrentlyOnline)
      } catch (error) {
        console.error('[APP] Bootstrap error:', error)
      }
    }

    bootstrap()
  }, [])

  // Web build only: the Electron bootstrap above returns early when there is no
  // preload, but the browser session cookie already exists, so resolve feature
  // flags here. Offline keeps whatever was persisted from the last session.
  useEffect(() => {
    if (hasElectronPreload()) return
    void (async (): Promise<void> => {
      const online = await getInternetStatus()
      void useFeatureFlagsStore.getState().fetchFlags(online)
    })()
  }, [])

  // Have a session id the server has checked free ready before the next Start
  // (utils/sessionIdSource). Asked again on sign-in, so the first guest after a
  // booth signs in does not fall back to a locally minted id.
  useEffect(() => {
    void prefetchSessionId()
    return useAuthStore.subscribe((state, prev) => {
      if (state.isAuthenticated && !prev.isAuthenticated) void prefetchSessionId()
    })
  }, [])

  // Fleet kill switch for the auto-updater. Main cannot read feature flags (they
  // are resolved per account, in the renderer), so push the value down whenever
  // it changes. Absent means enabled: a booth that cannot reach the backend must
  // keep updating itself.
  useEffect(() => {
    const push = (): void => {
      reportRemoteUpdateEnabled(useFeatureFlagsStore.getState().getFlag('auto_update', true))
    }
    push()
    return useFeatureFlagsStore.subscribe(push)
  }, [])

  /**
   * Liveness heartbeat. Cheap (one upserted row per device server-side), so it
   * runs every few minutes rather than hourly — the server derives its
   * offline-detection window from HEARTBEAT_INTERVAL_MINUTES, which the ping
   * itself reports. The full settings snapshot is sent separately and rarely
   * (View mount + settings save); it is the expensive, append-only one.
   *
   * Not gated on Electron: a Tauri/web booth is just as much a device to monitor.
   */
  useEffect(() => {
    const tick = (): void => {
      const projectId = useSessionStateStore.getState().projectId || undefined
      sendDeviceHeartbeat(projectId).catch((err) => {
        console.warn('[HEARTBEAT] Failed:', err)
      })
    }
    // Fire once on start rather than waiting a full period: a booth that is
    // restarted regularly could otherwise go long stretches without reporting.
    tick()
    const interval = setInterval(tick, HEARTBEAT_INTERVAL_MINUTES * 60 * 1000)
    return (): void => {
      clearInterval(interval)
    }
  }, [])

  /**
   * Card-terminal orders left open by a session the booth is no longer showing
   * (a reload, a crash, a power cut, a reset while a guest held the terminal).
   * The terminal keeps showing the amount until Mercado Pago expires the order,
   * so cancel it before someone pays it. First pass at start; a localStorage
   * read and nothing else while no order is open. See utils/openTerminalOrders.
   */
  useEffect(() => {
    const SWEEP_INTERVAL_MS = 20 * 1000
    const sweep = (): void => {
      sweepLeftoverTerminalOrders().catch((err) => {
        console.warn('[CARD TERMINAL] Leftover order sweep failed:', err)
      })
    }
    sweep()
    const interval = setInterval(sweep, SWEEP_INTERVAL_MS)
    return (): void => {
      clearInterval(interval)
    }
  }, [])

  /**
   * The goodbye. A booth closing for the night goes quiet exactly like one whose
   * power was cut, so without this the dashboard reported every booth that shut
   * down on time as Critical. In Electron the main process sends this on quit
   * (it outlives this window); here we cover the web/Tauri booth and the case of
   * a window closed without a full quit. Both paths are idempotent server-side.
   */
  useEffect(() => {
    const onLeave = (): void => {
      sendDeviceCloseSignal()
    }
    window.addEventListener('beforeunload', onLeave)
    return (): void => {
      window.removeEventListener('beforeunload', onLeave)
    }
  }, [])

  /**
   * Full settings snapshot on a slow timer. Each call INSERTS a document, so this
   * stays infrequent — but it must run at all: the dashboard discovers devices
   * from telemetry within a 7-day visibility window, and a booth left running for
   * longer without remounting View would otherwise age out of the list entirely
   * while still happily heartbeating.
   *
   * Fired once at startup as well as on the interval. Waiting a full six hours
   * for the first one meant a booth that never reaches View — one sitting on the
   * mode picker, or running in digital mode, where the mount snapshot is
   * deliberately skipped — sent its settings for the first time six hours after
   * launch, and had nothing but its heartbeat before then.
   */
  useEffect(() => {
    const SNAPSHOT_INTERVAL_MS = 6 * 60 * 60 * 1000
    const snapshot = (): void => {
      const projectId = useSessionStateStore.getState().projectId || undefined
      sendAppSettingsTelemetry({ projectId }).catch((err) => {
        console.warn('[TELEMETRY] Periodic snapshot failed:', err)
      })
    }
    snapshot()
    const interval = setInterval(snapshot, SNAPSHOT_INTERVAL_MS)
    return (): void => {
      clearInterval(interval)
    }
  }, [])

  return (
    <div>
      <OfflineIndicator isOnline={isOnline} />
      <AppRoutes />
    </div>
  )
}

export default App
